import React from 'react';
import { Box, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const PHASE_LABELS = {
  planning: '1 de 3 · Planeando',
  investigating: '2 de 3 · Investigando',
  synthesizing: '3 de 3 · Redactando el informe'
};

/**
 * Progreso de un Análisis Profundo mientras corre: la fase, el plan como
 * lista que se va marcando y el avance de las consultas. Recibe el estado
 * de deepProgress.js.
 */
const DeepAnalysisProgress = ({ progress }) => {
  const { phase, label, objective, steps, queries, maxQueries } = progress;
  const done = steps.filter((step) => step.done).length;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid var(--wb-border-20)',
        backgroundColor: 'var(--wb-overlay-08)',
        maxWidth: 720
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <CircularProgress size={20} sx={{ color: 'var(--wb-accent)' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'var(--wb-accent)', fontWeight: 600, letterSpacing: 0.4 }}>
            ANÁLISIS PROFUNDO · {PHASE_LABELS[phase] || ''}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--wb-text-strong)' }}>{label}</Typography>
        </Box>
      </Stack>

      {objective && (
        <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)', mt: 1.5 }}>
          Objetivo: {objective}
        </Typography>
      )}

      {steps.length > 0 && (
        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5 }} aria-label="Plan del análisis">
          {steps.map((step) => (
            <Stack component="li" key={step.id} direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.5 }}>
              {step.done ? (
                <CheckCircleIcon fontSize="small" sx={{ color: 'var(--wb-success)', mt: '2px' }} aria-label="Resuelto" />
              ) : (
                <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'var(--wb-text-ghost)', mt: '2px' }} aria-label="Pendiente" />
              )}
              <Typography variant="body2" sx={{ color: step.done ? 'var(--wb-text-strong)' : 'var(--wb-text-soft)' }}>
                {step.question}
              </Typography>
            </Stack>
          ))}
        </Box>
      )}

      {(steps.length > 0 || maxQueries) && phase === 'investigating' && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress
            variant="determinate"
            value={steps.length ? (done / steps.length) * 100 : 0}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'var(--wb-border-16)',
              '& .MuiLinearProgress-bar': { backgroundColor: 'var(--wb-accent)' }
            }}
          />
          <Typography variant="caption" sx={{ color: 'var(--wb-text-faint)', display: 'block', mt: 0.5 }}>
            {steps.length ? `${done} de ${steps.length} pasos` : ''}
            {maxQueries ? `${steps.length ? ' · ' : ''}${queries} de ${maxQueries} consultas como máximo` : ''}
          </Typography>
        </Box>
      )}

      <Typography variant="caption" sx={{ color: 'var(--wb-text-faint)', display: 'block', mt: 1.5 }}>
        Puede tardar algunos minutos. Puedes seguir en esta página mientras termina.
      </Typography>
    </Box>
  );
};

export default DeepAnalysisProgress;
